import React from 'react'
import PropTypes from 'prop-types'
import {
  Image,
  Text,
  Box,
  Columns,
  ThemeProvider,
  MediaQuery,
  CMSContent,
  Button,
  Link,
  Title,
} from '@deseretbook/react-ui'
import { getInfo } from '@deseretbook/cms'
import { capitalize } from '@deseretbook/utils'
import { RichText } from 'prismic-dom'
import { Conditional } from '../../core'
import { formatImageProps } from '../utils'
import { useMediaQuery, linkResolver, useTheme, useUserPermission } from '../'

/**
 * Simple Button link component.
 * @param {object} props - Component props
 * @returns {function} Component
 */
const ButtonLink = props => <Button as={Link} {...props} />

/**
 * Determines column sizes based on the given images slice.
 *
 * @param {object} [slice={}] - Images slices
 * @returns {object} column sizes
 */
const parseColumnSizes = (slice = {}) => {
  const {
    columnSize,
    mediumScreenColumnSize,
    smallScreenColumnSize,
    tinyScreenColumnSize,
  } = slice.primary || {}

  const sizes = {
    tiny: tinyScreenColumnSize || columnSize,
    small: smallScreenColumnSize || columnSize,
    medium: mediumScreenColumnSize || columnSize,
  }

  return Object.entries(sizes).reduce((applicableSizes, [key, value]) => {
    if (value) {
      return {
        ...applicableSizes,
        [key]: value,
      }
    }

    return applicableSizes
  }, {})
}

/**
 * An Images component that renders images slices. This component supports the following
 * slice types:
 * - images_v1
 *
 * @returns {function} Component
 */
const Images = ({ slice, user }) => {
  const { type } = getInfo(slice)

  if (type !== 'images') {
    return false
  }

  const theme = useTheme(slice.primary && slice.primary.theme)
  const mediaQuery = useMediaQuery(slice.primary && slice.primary.mediaQuery)
  const userPermission = useUserPermission(
    slice.primary && slice.primary.userPermission,
    user,
  )

  const columnSize = parseColumnSizes(slice)

  const isLoading =
    theme.isLoading || mediaQuery.isLoading || userPermission.isLoading

  if (!userPermission.hasPermission) {
    return false
  }

  if (isLoading) {
    return <Text>Loading...</Text>
  }

  return (
    <Conditional
      condition={Object.keys(theme.theme).length}
      component={ThemeProvider}
      props={{ theme: theme.theme }}
    >
      <Conditional
        condition={Object.keys(mediaQuery.mediaQuery).length}
        component={MediaQuery}
        props={mediaQuery.mediaQuery}
      >
        <Columns isMobile isCentered>
          {slice.items.map((item, idx) => (
            /* eslint-disable-next-line react/no-array-index-key */
            <Columns.Column key={idx} size={columnSize}>
              <Box marginBottom={0.5}>
                <Conditional
                  condition={
                    item.imageLink &&
                    item.imageLink.url &&
                    item.imageLink.linkType === 'Web'
                  }
                  component={Link}
                  props={{
                    href: item.imageLink.url,
                    target: item.imageLink.target,
                  }}
                >
                  <Image {...formatImageProps(item.image)} />
                </Conditional>
              </Box>
              {!!(item.title || item.subtitle) && (
                <Title
                  tile={item.title}
                  subtitle={item.subtitle}
                  alignment="center"
                  aria-level="3"
                />
              )}
              {!!(item.content && item.content.length) && (
                <Box marginVertical={0.5}>
                  <CMSContent
                    itsNotDangerousBecause="This html was generated by Prismic and Prismic doesn't allow writing html in their editor. 😎"
                    dangerouslySetInnerHTML={{
                      __html: RichText.asHtml(item.content, linkResolver),
                    }}
                  />
                </Box>
              )}
              {item.actionButtonLink &&
                item.actionButtonLink.url &&
                item.actionButtonLink.linkType === 'Web' &&
                item.actionButtonText && (
                  <Box justifyContent="center" marginVertical={0.5}>
                    <ButtonLink
                      {...{
                        [`is${capitalize(item.actionButtonStyle)}`]: true,
                      }}
                      href={item.actionButtonLink.url}
                      target={item.actionButtonLink.target}
                    >
                      {item.actionButtonText}
                    </ButtonLink>
                  </Box>
                )}
            </Columns.Column>
          ))}
        </Columns>
      </Conditional>
    </Conditional>
  )
}

Images.propTypes = {
  slice: PropTypes.shape({
    sliceType: PropTypes.string,
    primary: PropTypes.shape({
      title: PropTypes.string,
      subtitle: PropTypes.string,
    }),
  }),
  user: PropTypes.shape({}),
}

Images.defaultProps = {
  slice: {},
  user: null,
}

export default Images
